import './group-panel.less';
import React, { useEffect, useState } from 'react';
import { Button, Modal, Tabs, Tooltip } from '@meitu/saas-ui';
import { GroupLayerHotkeyType } from '@meitu/saas-editor-core/es/layers/group-layer';
import { groupAllText } from '@meitu/saas-editor-core/es/layers/group-layer/utils/layer';
import { LayerType } from '@meitu/saas-editor-core/es/version-types';
import { BaseToolbar } from '@meitu/saas-editor-ui/es/components/base-toolbar';
import { DividerLine } from '@meitu/saas-editor-ui/es/components/divider-line';
import { HeaderToolbar } from '@meitu/saas-editor-ui/es/components/header-toolbar';
import { useListenActivePickLayerProps } from '@meitu/saas-editor-ui/es/hooks/use-listen-layer-props';
import IconFont from '@meitu/xdesign-icon-font';
import { events } from '@/events-bus';
import { useGroupPanelLayerId, usePanelLayerId } from '@/hooks';
import { isEditAIPPTComponent, isEditAIPPTTemplate } from '@/hooks/fetch-template/editor-mode';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { panelConf } from '../../config';
import PPTMarkPanel from '../ppt-mark-panel';
import { useGroupAction } from './use-group-action';

const Controller: React.FC<{
    layerId: string;
    onCreateGroup?: () => void;
    onBreakGroup?: () => void;
}> = React.memo(({ layerId, onCreateGroup, onBreakGroup }) => {
    const { $t } = useLocaleStore();

    const action = useGroupAction(editor, layerId);
    if (!action) return null;

    return (
        <div className="c-group-panel__controller">
            {action === 'create' ? (
                <Button
                    className="c-group-panel__controller__make-group"
                    onClick={() => {
                        onCreateGroup?.();
                        editor.emitHotkey(GroupLayerHotkeyType.PACK);
                    }}
                    block
                    type="primary"
                    data-blue
                    icon={<IconFont name="FolderBold" fontSize={16} />}
                >
                    <div>{$t((v) => v.panel.groupPanel.makeGroup)}</div>
                </Button>
            ) : action === 'break' ? (
                <Button
                    className="c-group-panel__controller__broke-group"
                    onClick={() => {
                        onBreakGroup?.();
                        editor.emitHotkey(GroupLayerHotkeyType.UNPACK);
                    }}
                    block
                    type="primary"
                    data-blue
                    icon={<IconFont name="FolderBold" fontSize={16} />}
                >
                    <div>{$t((v) => v.panel.groupPanel.breakGroup)}</div>
                </Button>
            ) : (
                <Tooltip placement="bottom" title={$t((v) => v.panel.groupPanel.tips.breakGroupVipLimit)}>
                    <Button
                        className="c-group-panel__controller__broke-group"
                        disabled
                        block
                        type="primary"
                        data-blue
                        icon={<IconFont name="FolderBold" fontSize={16} />}
                    >
                        <div>{$t((v) => v.panel.groupPanel.breakGroup)}</div>
                    </Button>
                </Tooltip>
            )}
        </div>
    );
});

const MergeLayers: React.FC<{ layerId: string }> = React.memo(({ layerId }) => {
    const { $t } = useLocaleStore();
    const handleMergeLayer = () => {
        Modal.confirm({
            title: $t((v) => v.panel.groupPanel.tips.mergeConfirm),
            content: '',
            okText: $t((v) => v.panel.groupPanel.tips.okText),
            cancelText: $t((v) => v.panel.groupPanel.tips.cancelText),
            centered: true,
            closable: true,
            onOk: async () => {
                editor.services.mask.show({ loading: true });
                try {
                    editor.groupLayer.merge(layerId);
                } catch (error) {
                    console.log('error', error);
                } finally {
                    editor.services.mask.hide();
                }
            },
            onCancel: () => {},
        });
    };

    return (
        <div className="c-group-panel__controller">
            <Button
                onClick={handleMergeLayer}
                block
                className="c-group-panel__controller__merge-layer"
                icon={<IconFont name="LayerBold" fontSize={16} />}
            >
                <div>{$t((v) => v.panel.groupPanel.mergeLayer)}</div>
            </Button>
        </div>
    );
});

const Controllers: React.FC<{ id: string }> = React.memo(({ id }) => {
    return (
        <div className="c-group-panel__controllers">
            <Controller layerId={id} />
            <MergeLayers layerId={id} />
        </div>
    );
});
// 组内子面板
const AdditionPanel: React.FC<{ id: string }> = React.memo(({ id }) => {
    const layerType = useListenActivePickLayerProps(
        editor,
        (layer) => {
            if (!layer) return null;
            return layer.layerType;
        },
        false,
    );
    const [textGroup, setTextGroup] = useState(groupAllText(editor.services.flatLayer.getLayer(id)));
    useEffect(() => {
        setTextGroup(groupAllText(editor.services.flatLayer.getLayer(id)));
        const handler = editor.services.frame.onActiveLayerDataUpdate((layer) => {
            if (!layer) return;
            setTextGroup(groupAllText(layer));
        });
        return () => {
            editor.off(handler);
        };
    }, [id]);

    const renderPanel = (type: LayerType) => (
        <React.Fragment>
            <DividerLine />
            <div className="c-group-panel__pick-layer">{panelConf[type]}</div>
        </React.Fragment>
    );

    if (layerType === LayerType.GROUP_LAYER) return null;

    return layerType ? renderPanel(layerType) : textGroup ? renderPanel(LayerType.TEXT_LAYER) : <Spacing />;
});

const Spacing = React.memo(() => {
    return <div className="c-group-panel__spacing"></div>;
});

const pptMarkTabsList = [
    {
        name: '组合',
        tabKey: 'bg',
    },
    {
        name: 'ppt打标',
        tabKey: 'pptMark',
    },
];

const RenderTabs: React.FC<{
    showMaskContent: boolean;
    setShowMaskContent: (flag: boolean) => void;
    isPPT?: boolean;
}> = ({ isPPT = false, setShowMaskContent }) => {
    const [tabIndex, setTabIndex] = useState(1);
    const handleTabClick = (key: string) => {
        setTabIndex(key === 'bg' ? 1 : 2);
        setShowMaskContent(key !== 'bg');
    };

    return (
        <div style={{ padding: '0 var(--gap-lg)' }}>
            <Tabs
                showContent={false}
                data={isPPT ? pptMarkTabsList : []}
                tabsWidth="228px"
                tabsHeight="34px"
                handleTabClick={handleTabClick}
                activeIndex={tabIndex}
            />
        </div>
    );
};

const isAIPPTMark = isEditAIPPTComponent() || isEditAIPPTTemplate();
const GroupPanel = () => {
    const layerId = useGroupPanelLayerId();
    const [showMaskContent, setShowMaskContent] = useState(false);
    const subLayerId = usePanelLayerId();
    if (!layerId) return null;

    return (
        <div className="c-group-panel">
            {isAIPPTMark ? (
                <div style={{ marginTop: '16px' }}>
                    <RenderTabs
                        showMaskContent={showMaskContent}
                        setShowMaskContent={setShowMaskContent}
                        isPPT={isAIPPTMark}
                    />
                </div>
            ) : null}
            <div
                style={{
                    display: `${isAIPPTMark && showMaskContent ? 'none' : 'block'}`,
                }}
            >
                <HeaderToolbar id={layerId} editor={editor} inGroup={true} events={events} style={{ padding: 16 }} />
                <Controllers id={layerId} />

                {/* 组内子面板 */}
                <AdditionPanel id={layerId} />

                <BaseToolbar id={layerId} editor={editor} inGroup={true} style={{ padding: '0 16px 16px' }} />
            </div>
            {isAIPPTMark && showMaskContent ? <PPTMarkPanel editor={editor} key={subLayerId} /> : null}
        </div>
    );
};

export default React.memo(GroupPanel);
